import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobArchitecture, JobArchitectureSummary } from '~/shared/data-contract/job-architecture';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export const useCreateJobArchitecture = (): UseMutationResult<JobArchitecture, Error, void> => {
    const queryClient = useQueryClient();
    const { createJobArchitecture, patchJobArchitectureOnboardingState } = useApplicationFetchers();
    const { applicationState } = useApplicationContext();
    const { featureFlagService } = applicationState;

    return useMutation({
        mutationFn: async () => {
            const result = await createJobArchitecture();

            if (result.isErr === true) {
                throw result.error;
            }

            if (featureFlagService.isFeatureFlagEnabled('jsa-review-mode')) {
                /** The onboarding state is being set here, for the time being,
                 **  we might move this in the backend soon */
                await patchJobArchitectureOnboardingState({
                    jobArchitectureId: result.value.id,
                    onboardingState: {
                        familiesAccepted: false,
                        levelsAccepted: false,
                        metadataCompleted: false,
                        onboardingCompleted: false,
                        reviewersAssigned: false,
                        rolesAccepted: false,
                    },
                });
            }

            return result.value;
        },
        onSuccess: (newJobArchitecture) => {
            queryClient.cancelQueries({
                queryKey: jobArchitectureKeys.architectures(),
            });
            queryClient.setQueryData(
                jobArchitectureKeys.architectures(),
                (old: JobArchitectureSummary[] | undefined) => [...(old ?? []), newJobArchitecture],
            );
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectures(),
            });
        },
    });
};
